import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Alert, Loader, Badge, Select, Table, Dialog } from '@dreamtree-org/twreact-ui';
import { Github as GithubIcon, LinkIcon, Unlink, RefreshCw, ExternalLink } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';

const errText = (ex, fallback) => (ex.response && ex.response.data && ex.response.data.error) || ex.message || fallback;

/**
 * GitHub › connection.
 *
 * Shows whether the integration is configured on this deployment (separate from whether
 * THIS user has connected), starts the OAuth hand-off, and lets a connected user pick the
 * repo + branch they work against by default.
 *
 * No token ever reaches this screen — the model strips the encrypted columns from every
 * read, and the exchange happens server-side in the callback.
 */
export default function Github() {
  const { fetchApi } = useAppApi();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const [repos, setRepos] = useState(null);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [branches, setBranches] = useState([]);
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('');
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi('githubStatus');
      const d = res?.data || res || {};
      setStatus(d);
      setRepo(d.default_repo || '');
      setBranch(d.default_branch || '');
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not read the GitHub connection'));
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setErr('');
    setConnecting(true);
    try {
      const res = await fetchApi('githubConnectStart');
      const url = res?.data?.url;
      if (!url) throw new Error('No consent URL returned');
      // Full navigation, not a new tab: GitHub redirects back to /github/callback and a
      // popup would leave the opener showing stale status.
      window.location.href = url;
    } catch (ex) {
      setErr(errText(ex, 'Could not start the GitHub connect flow'));
      setConnecting(false);
    }
  };

  const loadRepos = async () => {
    setErr('');
    setLoadingRepos(true);
    try {
      const res = await fetchApi('githubListRepos');
      setRepos(res?.data?.repos || []);
    } catch (ex) {
      setErr(errText(ex, 'Could not list repositories'));
    } finally {
      setLoadingRepos(false);
    }
  };

  const chooseRepo = async (fullName) => {
    setRepo(fullName);
    setBranch('');
    setBranches([]);
    try {
      const res = await fetchApi('githubListBranches', { repo: fullName });
      setBranches(res?.data?.branches || []);
    } catch (ex) {
      setErr(errText(ex, 'Could not list branches'));
    }
  };

  const save = async () => {
    setErr('');
    setSaving(true);
    try {
      await fetchApi('githubSetRepo', { repo: repo || null, branch: branch || null });
      setNotice('Default repository saved.');
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not save the default repository'));
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    setErr('');
    try {
      await fetchApi('githubDisconnect');
      setConfirmDisconnect(false);
      setRepos(null);
      setNotice('GitHub disconnected.');
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not disconnect'));
    }
  };

  if (loading) return <Loader text="Loading GitHub connection…" />;

  const repoColumns = [
    {
      key: 'full_name',
      label: 'Repository',
      render: (r) => (
        <div className="flex items-center gap-2">
          <button type="button" className="text-primary-600 font-medium hover:underline" onClick={() => chooseRepo(r.full_name)}>
            {r.full_name}
          </button>
          {r.private ? <Badge variant="secondary">private</Badge> : null}
        </div>
      ),
    },
    { key: 'default_branch', label: 'Default branch' },
    {
      key: 'html_url',
      label: '',
      render: (r) => (
        r.html_url
          ? <a href={r.html_url} target="_blank" rel="noreferrer" className="opacity-50 hover:opacity-100" title="Open on GitHub"><ExternalLink size={15} /></a>
          : null
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <GithubIcon size={22} className="text-primary-600" />
        <div>
          <h1 className="text-xl font-bold">GitHub</h1>
          <p className="text-sm opacity-70">Connect a GitHub account so this app can read your repositories.</p>
        </div>
      </div>

      {err ? <Alert variant="error" title="GitHub">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="GitHub">{notice}</Alert> : null}

      {!status?.configured ? (
        <Alert variant="warning" title="Not configured on this server">
          Set GITHUB_CLIENT_ID, GITHUB_CLIENT_SECRET and GITHUB_REDIRECT_URI, then restart the
          API. Until then the connect flow is unavailable.
        </Alert>
      ) : null}

      <Card className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Badge variant={status?.connected ? 'success' : 'default'}>
              {status?.connected ? 'connected' : 'not connected'}
            </Badge>
            {status?.login ? <span className="text-sm font-mono">@{status.login}</span> : null}
            {status?.installation_id ? <Badge variant="secondary">app installed</Badge> : null}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="opacity-50 hover:opacity-100" onClick={load} title="Refresh">
              <RefreshCw size={15} />
            </button>
            {status?.connected ? (
              <Can module="github" action="disconnect">
                <Button variant="destructive" leftIcon={<Unlink size={16} />} onClick={() => setConfirmDisconnect(true)}>
                  Disconnect
                </Button>
              </Can>
            ) : (
              <Can module="github" action="connect">
                <Button
                  leftIcon={<LinkIcon size={16} />}
                  onClick={connect}
                  loading={connecting}
                  disabled={!status?.configured}
                >
                  Connect GitHub
                </Button>
              </Can>
            )}
          </div>
        </div>

        {/* Only an App installation can see org-owned private repos; a plain OAuth grant
            cannot, which reads as "my repo is missing" without this hint. */}
        {status?.connected && status?.install_url ? (
          <p className="text-xs opacity-60">
            Missing an organisation&apos;s repositories?{' '}
            <a className="text-primary-600 hover:underline" href={status.install_url} target="_blank" rel="noreferrer">
              Install the GitHub App
            </a>{' '}
            on that account.
          </p>
        ) : null}
      </Card>

      {status?.connected ? (
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-sm font-semibold">Default repository</h2>
              <p className="text-xs opacity-60">
                Currently {status.default_repo
                  ? <span className="font-mono">{status.default_repo}{status.default_branch ? `@${status.default_branch}` : ''}</span>
                  : 'not set'}.
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={loadRepos} loading={loadingRepos} leftIcon={<RefreshCw size={14} />}>
              {repos ? 'Reload repositories' : 'Load repositories'}
            </Button>
          </div>

          {repo ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <span className="block text-sm font-medium mb-1">Selected</span>
                <p className="font-mono text-sm">{repo}</p>
              </div>
              {/* twreact's Select passes the RAW value to onChange, not a DOM event. */}
              <Select
                label="Branch"
                value={branch}
                options={branches.map((b) => ({ value: b.name, label: b.protected ? `${b.name} (protected)` : b.name }))}
                onChange={(v) => setBranch(v?.target ? v.target.value : (v?.value ?? v))}
                placeholder={branches.length ? 'Pick a branch' : 'No branches loaded'}
              />
            </div>
          ) : null}

          {repos ? (
            <Table data={repos} columns={repoColumns} pagination globalSearch withAction={false} />
          ) : (
            <p className="text-sm opacity-50">Load your repositories to pick a default.</p>
          )}

          <div className="flex justify-end">
            <Button onClick={save} loading={saving} disabled={!repo}>Save default</Button>
          </div>
        </Card>
      ) : null}

      {confirmDisconnect ? (
        <Dialog isOpen onClose={() => setConfirmDisconnect(false)} title="Disconnect GitHub?">
          <div className="space-y-4">
            <p className="text-sm">
              The stored access token is deleted. Nothing on GitHub changes, and you can reconnect
              at any time.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
              <Button variant="destructive" onClick={disconnect}>Disconnect</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
